import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { logger } from 'utils/logger';

// Create a component-specific logger
const log = logger.getLogger('ProtectedRoute');

/**
 * Loading spinner component with optional message
 */
const LoadingSpinner: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      gap: 2
    }}
  >
    <CircularProgress size={40} />
    {message && <Typography variant="body1" color="text.secondary">{message}</Typography>}
  </Box>
);

/**
 * ProtectedRoute component that guards routes requiring authentication
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute: React.FC = () => {
  const { auth } = useAuth();
  const location = useLocation();
  
  // Log route access attempts for debugging
  React.useEffect(() => {
    log.debug(`Route access attempt: ${location.pathname}`);
  }, [location.pathname]);
  
  // Show loading spinner while checking authentication
  if (auth.isLoading) {
    return <LoadingSpinner message="Verifying authentication..." />;
  }
  
  // If not authenticated, save current location and redirect to login
  if (!auth.isAuthenticated) {
    // Store the current path to redirect back after login
    const returnPath = location.pathname + location.search;
    sessionStorage.setItem('returnTo', returnPath);
    
    log.info(`Redirecting unauthenticated user to login from: ${returnPath}`);
    
    // Redirect to login page
    return <Navigate to="/login" replace />;
  }
  
  // Render children routes if authenticated
  log.debug(`User authenticated, rendering protected route: ${location.pathname}`);
  return <Outlet />;
};

export default ProtectedRoute;